/**
 * =========================================================
 * DACI Frontend - Componente de Paginación Personalizado
 * =========================================================
 * 
 * Componente wrapper que encapsula el componente Pagination de MUI
 * con soporte automático para tema oscuro y estilos consistentes
 * en toda la aplicación.
 * 
 * Características:
 * - Soporte automático para tema oscuro/claro
 * - Estilos consistentes en toda la aplicación
 * - Fácil mantenimiento centralizado
 * - Compatible con todas las props de MUI Pagination
 */

import React from "react";
import { Pagination } from "@mui/material";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";

/**
 * Componente de paginación personalizado con soporte para tema oscuro
 * 
 * @param {Object} props - Props del componente
 * @param {Object} props.sx - Estilos adicionales (opcional)
 * @param {...any} rest - Resto de props que se pasan al componente Pagination de MUI
 * @returns {JSX.Element} Componente de paginación con tema aplicado
 */
const DACIPagination = ({ sx = {}, ...rest }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Estilos base para el tema oscuro/claro
  const baseStyles = {
    "& .MuiPaginationItem-root": {
      color: darkMode ? "#ffffff" : "#344767",
      "&:hover": {
        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(52, 71, 103, 0.1)",
      },
    },
    "& .MuiPaginationItem-root.Mui-selected": {
      backgroundColor: darkMode ? "#1976d2 !important" : "#1976d2 !important",
      color: "#ffffff !important",
      "&:hover": {
        backgroundColor: darkMode ? "#1565c0 !important" : "#1565c0 !important",
      },
    },
    "& .MuiPaginationItem-ellipsis": {
      color: darkMode ? "#ffffff" : "#344767",
    },
    // Estilos para botones de navegación (anterior/siguiente)
    "& .MuiPaginationItem-previousNext": {
      color: darkMode ? "#ffffff" : "#344767",
    },
    ...sx, // Permitir estilos adicionales
  };

  return (
    <Pagination
      sx={baseStyles}
      {...rest}
    />
  );
};

DACIPagination.propTypes = {
  /**
   * Estilos adicionales para el componente
   */
  sx: PropTypes.object,
};

DACIPagination.defaultProps = {
  sx: {},
};

export default DACIPagination;